import { getIn } from 'final-form';
import type { Decorator } from 'final-form';
import { equals } from 'ramda';

/**
 * Computes the new value of a target field. Returning a promise defers the `form.change`.
 * The parameters are `never` so each module can declare their real types: a shared record cannot
 * know them, and `unknown` in that contravariant position would reject every precise annotation.
 */
type UpdateFn = (
  newValue: never,
  allValues: never,
  initialValues: never,
  previousValue: never,
) => unknown;

/** One watched field and the fields it writes to when it changes. */
export type DecoratorEffect = {
  field: string;
  updates: Record<string, UpdateFn>;
};

const createSideEffectDecorator =
  <FormValues = object, InitialFormValues = Partial<FormValues>>(
    args: DecoratorEffect[],
    onSubscribed?: () => unknown,
  ): Decorator<FormValues, InitialFormValues> =>
    form => {
      let previous: unknown;
      const initializedPromise = typeof onSubscribed === 'function' ? onSubscribed() : Promise.resolve();
      const unsubscribe = form.subscribe(
        async ({ values, initialValues }) => {
          await initializedPromise;

          if (!previous) {
            previous = initialValues;
          }

          const sideEffectPromises: Array<{ field: string; promise: Promise<unknown> }> = [];
          form.batch(() => {
            args.forEach(({ field, updates }) => {
              const newValue = getIn(values as object, field);
              const previousValue = getIn(previous as object, field);

              if (!equals(newValue, previousValue)) {
                Object.entries(updates).forEach(([updateField, updateFn]) => {
                  const updateValue = (updateFn as (...params: unknown[]) => unknown)(newValue, values, initialValues, previousValue);

                  if (updateValue instanceof Promise) {
                    sideEffectPromises.push({ field: updateField, promise: updateValue });
                  } else {
                    form.change(updateField as never, updateValue as never);
                  }
                });
              }
            });
          });

          const promises = sideEffectPromises.map(({ promise }) => promise);
          Promise.all(promises).then(resolvedValues => {
            form.batch(() => {
              resolvedValues.forEach((resolvedValue, index) => {
                form.change(sideEffectPromises[index].field as never, resolvedValue as never);
              });
            });
          });
          previous = values;
        },
        { values: true, initialValues: true }
      );
      return unsubscribe;
    };

export default createSideEffectDecorator;
